// src/components/Catalog/ShirtsGrid.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import ShirtCard from "./ShirtCard";
import AuctionShirtCard from "./AuctionShirtCard";
import { ApiShirt } from "../../types/Shirt";
import type { Auction } from "../../services/auctionService";

interface ShirtItem {
  id?: number | string;
}

export interface ShirtsGridProps {
  items?: ShirtItem[];
  apiShirts?: ApiShirt[];
  auctionMap?: Map<string, Auction>;
  visible?: number;
  canLoadMore?: boolean;
  onLoadMore?: () => void;
  gridId?: string;
  label?: string;
  isAuction?: boolean;
  /** Toont een "Filters wissen"-knop in de lege staat. */
  onClearFilters?: () => void;
}

export default function ShirtsGrid({
  items = [],
  apiShirts,
  auctionMap,
  visible,
  canLoadMore = false,
  onLoadMore = () => {},
  gridId = "shirtsGrid",
  label = "Shirts",
  isAuction = false,
  onClearFilters,
}: ShirtsGridProps) {
  const { t } = useTranslation();

  // If real API data is provided, render from that
  if (apiShirts !== undefined) {
    const visibleShirts = visible ? apiShirts.slice(0, visible) : apiShirts;
    return (
      <section className="shirts" aria-label={label}>
        <div className="shirts-grid" id={gridId} tabIndex={-1}>
          {visibleShirts.length === 0 ? (
            <div className="catalog-empty" role="status">
              <div className="catalog-empty__icon" aria-hidden="true">
                <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round">
                  <path d="M22 8 L10 15 L15 27 L21 24 V54 H43 V24 L49 27 L54 15 L42 8 C40 12 36 14 32 14 C28 14 24 12 22 8 Z" />
                </svg>
              </div>
              <h3 className="catalog-empty__title">{t("catalog.emptyTitle")}</h3>
              <p className="catalog-empty__hint">{t("catalog.emptyHint")}</p>
              {onClearFilters && (
                <button
                  type="button"
                  className="catalog-empty__clear"
                  onClick={onClearFilters}
                >
                  {t("catalog.emptyClear")}
                </button>
              )}
            </div>
          ) : (
            visibleShirts.map((shirt) => {
              const isShirtAuction = shirt.sales_type === "auction";
              return isShirtAuction ? (
                <AuctionShirtCard key={shirt.id} shirt={shirt} auction={auctionMap?.get(shirt.id)} />
              ) : (
                <ShirtCard key={shirt.id} shirt={shirt} />
              );
            })
          )}
        </div>

        {canLoadMore && (
          <button
            id="loadMore"
            className="btn load-more"
            aria-controls={gridId}
            onClick={onLoadMore}
          >
            Meer laden
          </button>
        )}
      </section>
    );
  }

  // Fallback: placeholder items
  const effectiveVisible = visible ?? items.length;
  return (
    <section className="shirts" aria-label={label}>
      <div className="shirts-grid" id={gridId} tabIndex={-1}>
        {items.slice(0, effectiveVisible).map((item, index) => {
          const id = item?.id ?? index;
          return isAuction ? (
            <AuctionShirtCard key={id} idx={Number(id)} />
          ) : (
            <ShirtCard key={id} idx={Number(id)} />
          );
        })}
      </div>

      {canLoadMore && (
        <button
          id="loadMore"
          className="btn load-more"
          aria-controls={gridId}
          onClick={onLoadMore}
        >
          Meer laden
        </button>
      )}
    </section>
  );
}
